import { useTranslation } from 'next-i18next';

import { useForm } from 'react-hook-form';

import {
    Box,
    Button,
    Field,
    Flex,
    Heading,
    HStack,
    Input,
    Separator,
    Text,
    useSlotRecipe,
} from '@chakra-ui/react';

import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingState } from '@nocowanie/core/enums';
import { IconsLinear } from '@nocowanie/icons/linear';

import {
    useNewsletterApi,
    useNewsletterUi,
} from '@app/components/organisms/newsletter/newsletter.hooks';
import { SalesManagoTag } from '@app/enums';
import { environment } from '@app/environments/environment';

import { getSchema } from './newsletter.def';

// const ChakraNextImage = dynamic(() =>
//     import('@app/components/atoms/chakra-next-image').then(c => c.ChakraNextImage),
// );

export const Newsletter = () => {
    const recipe = useSlotRecipe({ key: 'newsletter' });
    const styles = recipe({});

    // const imgSrc = urlJoin(environment.ASSETS_PATH, 'svg', 'sparkus.svg');

    const { t } = useTranslation('home', { keyPrefix: 'newsletter' });
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(getSchema(t)),
    });
    const { loadingState, onSubmit } = useNewsletterApi(SalesManagoTag.HOME_PAGE);
    const { componentStyle, translation } = useNewsletterUi(loadingState);

    return (
        <Flex
            data-testid={'newsletter-wrapper'}
            bg={componentStyle.bg}
            mb={componentStyle.mb}
            css={styles.container}
        >
            <Box css={styles.formWrapper} alignSelf={componentStyle.alignSelf}>
                {loadingState === LoadingState.ERROR ? (
                    <>
                        <Heading as={'h2'} my={4}>
                            {t('errorTitle')}
                        </Heading>
                        <Text maxW={'600px'}>{t('errorDescription')}</Text>
                    </>
                ) : (
                    <>
                        <Heading as={'h2'} hideBelow={'md'} css={styles.headingLg}>
                            {translation.titleLg}
                        </Heading>
                        <Heading
                            as={'h2'}
                            hideFrom={'md'}
                            dangerouslySetInnerHTML={{ __html: translation.titleSm }}
                            css={styles.headingSm}
                        />
                        <Text
                            fontSize={{
                                base: 'md',
                                xl: 'lg',
                            }}
                            maxW={'600px'}
                            dangerouslySetInnerHTML={{ __html: translation.description }}
                        />
                    </>
                )}
                {loadingState !== LoadingState.SUCCESS ? (
                    <>
                        <HStack
                            as={'form'}
                            data-testid="newsletter-form"
                            onSubmit={handleSubmit(onSubmit)}
                            css={styles.form}
                        >
                            <Field.Root css={styles.formInputWrapper} hideBelow={'md'}>
                                <Input
                                    css={styles.formInput}
                                    size="md"
                                    {...register('name')}
                                    data-testid="newsletter-form-name-input"
                                    placeholder={t('namePlaceholder')}
                                />
                            </Field.Root>
                            <Separator
                                orientation={'vertical'}
                                hideBelow={'md'}
                                css={styles.formDivider}
                            />
                            <Field.Root css={styles.formInputWrapper} invalid={!!errors.email}>
                                <Input
                                    css={styles.formInput}
                                    size="md"
                                    data-testid="newsletter-form-email-input"
                                    placeholder={t('emailPlaceholder')}
                                    {...register('email')}
                                />
                                <Field.ErrorText
                                    css={styles.emailErrorMsg}
                                    data-testid={'newsletter-validation-error-message'}
                                >
                                    {errors.email?.message}
                                </Field.ErrorText>
                            </Field.Root>
                            <Button
                                type="submit"
                                data-testid="newsletter-form-submit-button"
                                variant={'solid'}
                                disabled={!!errors.email}
                                loading={loadingState === LoadingState.PENDING}
                                aria-label={t('submitButton')}
                                css={styles.button}
                            >
                                <IconsLinear.Add hideBelow={'md'} mr={2} />
                                <Text as={'span'} hideBelow={'md'}>
                                    {t('submitButton')}
                                </Text>
                                <IconsLinear.Send hideFrom={'md'} fontSize="2xl" />
                            </Button>
                        </HStack>
                        <Text
                            css={{ ...styles.privacyPolicyTxt }}
                            dangerouslySetInnerHTML={{
                                __html: `${t('privacyPolicy', {
                                    privacyPolicySzallas: environment.CONSENTS_URLS.privacyPolicy,
                                })}`,
                            }}
                        />
                    </>
                ) : null}
            </Box>
        </Flex>
    );
};
